import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { riskApi } from '../../api/riskApi';
import { WizardStepInput } from '../../components/Risk/WizardStepInput';
import { WizardStepClasses } from '../../components/Risk/WizardStepClasses';
import { WizardStepVariants } from '../../components/Risk/WizardStepVariants';
import { ArrowLeft } from 'lucide-react';

export const RiskWizard = () => {
    const { token } = useAuth();
    const navigate = useNavigate();

    // Core state
    const [step, setStep] = useState(1);
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [classes, setClasses] = useState([]);
    const [variants, setVariants] = useState([]);

    // UI state
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const handleNextToClasses = async () => {
        if (!name.trim() || !description.trim()) {
            setError("Nombre y descripción son requeridos.");
            return;
        }
        setError(null);
        setLoading(true);
        try {
            const res = await riskApi.classify(token, description);
            // Default select top suggestions (or empty if zero)
            const suggestedNums = (res.suggestions || []).map(s => s.class_number);
            setClasses(suggestedNums);
            setStep(2);
        } catch (err) {
            console.error("Classification error", err);
            setClasses([]); // Fallback to empty state
            setStep(2);
        } finally {
            setLoading(false);
        }
    };

    const handleNextToVariants = async () => {
        if (classes.length === 0) {
            setError("Debes seleccionar al menos una clase.");
            return;
        }
        setError(null);
        setLoading(true);
        try {
            const res = await riskApi.generateVariants(token, name);
            setVariants(res.variants || [name]);
            setStep(3);
        } catch (err) {
            console.error("Variants error", err);
            setVariants([name]); // Fallback safely to current exact name
            setStep(3);
        } finally {
            setLoading(false);
        }
    };

    const handleExecuteSearch = async () => {
        if (variants.length === 0) {
            setError("Debes tener al menos una variante para buscar.");
            return;
        }
        setError(null);
        setLoading(true);
        try {
            const payload = {
                proposed_name: name,
                business_description: description,
                confirmed_classes: classes,
                confirmed_variants: variants
            };
            const res = await riskApi.startSearch(token, payload);
            if (res.job_id) {
                // Instantly navigate to polling page
                navigate(`/risk/report/${res.job_id}`);
            }
        } catch (err) {
            console.error("Execution error", err);
            setError(err.response?.data?.detail || "Ocurrió un error al iniciar el análisis.");
            setLoading(false); // Only set to false if it failed. If success, we navigate away.
        }
    };

    return (
        <div className="max-w-4xl mx-auto py-8">
            <button
                onClick={() => navigate('/risk')}
                className="flex items-center text-sm text-gray-500 hover:text-gray-900 mb-6 transition"
            >
                <ArrowLeft className="h-4 w-4 mr-1" />
                Volver al Historial
            </button>

            <div className="bg-white rounded-xl shadow border border-gray-200 p-6 md:p-8">
                {/* Stepper Header */}
                <div className="flex flex-col md:flex-row gap-4 justify-between items-center mb-10 border-b border-gray-100 pb-6">
                    <h2 className="text-xl font-bold text-gray-900">
                        {step === 1 && "1. Datos de la Marca"}
                        {step === 2 && "2. Clasificación de Niza"}
                        {step === 3 && "3. Variaciones de Búsqueda"}
                    </h2>
                    <div className="flex items-center gap-2 text-sm text-gray-500 font-medium">
                        Paso {step} de 3
                    </div>
                </div>

                {error && (
                    <div className="mb-6 p-4 bg-red-50 text-red-700 border border-red-200 rounded-md text-sm">
                        {error}
                    </div>
                )}

                {step === 1 && (
                    <WizardStepInput
                        name={name}
                        setName={setName}
                        description={description}
                        setDescription={setDescription}
                        onNext={handleNextToClasses}
                        loading={loading}
                    />
                )}

                {step === 2 && (
                    <WizardStepClasses
                        classes={classes}
                        setClasses={setClasses}
                        onNext={handleNextToVariants}
                        onBack={() => { setStep(1); setError(null); }}
                        loading={loading}
                    />
                )}

                {step === 3 && (
                    <WizardStepVariants
                        variants={variants}
                        setVariants={setVariants}
                        onExecute={handleExecuteSearch}
                        onBack={() => { setStep(2); setError(null); }}
                        loading={loading}
                    />
                )}
            </div>
        </div>
    );
};
